// Doctor card — health-check summary surfaced on the dashboard so
// the operator notices a broken state without remembering to run
// `mu doctor`. Aimed at the "btop / k9s health badge" UX: when
// every check is OK, the card is a quiet one-liner; when something
// is warn or fail, the failing rows surface the moment they appear.
//
// Per feat_card_9_doctor (workstream `tui-impl`), promoted from the
// LAST reserved non-zero slot in design_global_keymap. Slot 0 is now
// Commits; DAG is keybind-only.
//
// CARD LAYOUT
//   glyph    check        STATUS   detail
//   <warn>   agents       warn     2 ghost panes; run `mu agent reconcile`
//   <fail>   workspaces   fail     1 orphan dir blocking spawns
//
// Glyph + colour priority: GLYPH.fail (red), GLYPH.warn (yellow), then
// GLYPH.ok (green). The card BODY filters to non-OK rows; when nothing
// is wrong, the card renders one quiet all-healthy row so the operator's
// eye learns to read the presence of rows as "something needs attention."
//
// CLIPPING POLICY (per feat_column_aligned_lists)
//   glyph, check, STATUS  → PROTECT (identifier; yankable check name)
//   detail                → CLIP    (free-form prose; can truncate)
//
// SUBTITLE
//   problemCount === 0  → "all healthy"
//   problemCount > 0    → "<problemCount>"
//
// DATA
//   - snapshot.doctor is populated by loadWorkstreamSnapshot when
//     called with `withDoctor: true`. The TUI's poll-loop hook
//     (src/cli/tui/state.ts) opts in. Static `mu state` and `mu
//     doctor` itself do not — `mu doctor`'s textual card is the
//     authoritative full diagnostic; this card is the dashboard
//     SIGNAL only. See src/doctor-summary.ts for the SDK seam.
//
// POPUP / FUTURE OBLIGATIONS (when slot-9 popup ships)
//   Out of scope NOW. Tracked by feat_more_cards_umbrella. When the
//   popup lands it MAY consume feat_popup_search_filter (`/`
//   filtering check names is useful) but NOT
//   feat_track_drill_chains_to_task_drill (rows aren't tasks). A
//   reasonable Enter-drill leaf is "show the full doctor card for
//   this check" or a one-line remediation hint; that's the popup
//   task's call. Until then, Shift+9 (`(`) stays a reserved noop.

import { Text } from "ink";
import type { ReactElement } from "react";
import type { DoctorCheck } from "../../../doctor-summary.js";
import { GLYPH } from "../../../glyphs.js";
import type { WorkstreamSnapshot } from "../../../state.js";
import {
  type ColumnSpec,
  contentWidthFromCols,
  layoutColumns,
  renderRow,
  termColsForLayout,
} from "../columns.js";
import { CARD_CONFIGS, cardRenderHeight } from "../layout.js";
import { ListRow } from "../list-row.js";
import { TitledBox } from "../titled-box.js";
import { CardPlaceholder } from "./_placeholder.js";

export interface DoctorCardProps {
  snapshot: WorkstreamSnapshot | null;
  rowBudget?: number;
  cols?: number;
}

export const cardConfig = CARD_CONFIGS[9];

const COLUMN_SPECS: ReadonlyArray<ColumnSpec> = [
  { kind: "protect" }, // glyph
  { kind: "protect" }, // check name
  { kind: "protect" }, // status (ok / warn / fail)
  { kind: "clip", min: 1 }, // detail (free-form)
];

export function DoctorCard({ snapshot, rowBudget, cols }: DoctorCardProps): ReactElement {
  const contentWidth = contentWidthFromCols(cols ?? termColsForLayout());
  if (snapshot === null || snapshot.doctor === null) {
    return CardPlaceholder({
      title: "Doctor",
      cardId: 9,
      config: cardConfig,
      rowBudget,
      cols,
      text: "loading…",
    });
  }

  const { checks, problemCount } = snapshot.doctor;
  const subtitle = formatSubtitle(problemCount);

  // Healthy path: render the quiet "GLYPH.ok <K> checks" line so the
  // operator can confirm the card ran (vs. simply being empty).
  if (problemCount === 0) {
    return CardPlaceholder({
      title: "Doctor",
      cardId: 9,
      config: cardConfig,
      rowBudget,
      cols,
      subtitle,
      children: (
        <Text dimColor>
          <Text color="green">{GLYPH.ok}</Text> {checks.length} check
          {checks.length === 1 ? "" : "s"}
        </Text>
      ),
    });
  }

  const problems = checks.filter((c) => c.status !== "ok");
  const shown = problems.slice(0, rowBudget ?? cardConfig.maxRows);
  const more = problems.length - shown.length;
  const bottomLabel = more > 0 ? `+${more} more · Shift+9` : undefined;
  const rows = shown.map((c) => [glyphFor(c), c.name, c.status, c.detail]);
  const widths = layoutColumns(rows, COLUMN_SPECS, contentWidth);

  return (
    <TitledBox
      height={cardRenderHeight(cardConfig, rowBudget)}
      width={cols}
      title="Doctor"
      subtitle={subtitle}
      cardId={9}
      bottomLabel={bottomLabel}
    >
      {shown.map((c, i) => {
        const row = rows[i];
        if (row === undefined) return null;
        const padded = renderRow(row, widths, COLUMN_SPECS);
        const statusColor = colorForStatus(c.status);
        const colors = [
          { color: statusColor }, // glyph
          { bold: true }, // name
          { color: statusColor }, // status
          { dimColor: true }, // detail
        ];
        return <ListRow key={c.name} cells={padded} contentWidth={contentWidth} colors={colors} />;
      })}
    </TitledBox>
  );
}

// ─── pure helpers (exported for unit tests) ────────────────────────

/** Per-row glyph driven by the check's status, from src/glyphs.ts —
 *  the same constants the Workspaces card and the agent status map
 *  use, so one symbol means one thing everywhere. */
export function glyphFor(c: Pick<DoctorCheck, "status">): string {
  switch (c.status) {
    case "fail":
      return GLYPH.fail;
    case "warn":
      return GLYPH.warn;
    case "ok":
      return GLYPH.ok;
  }
}

/** Picocolors-compatible colour name for the status. fail → red,
 *  warn → yellow, ok → green. Used both for the glyph and the
 *  STATUS column so the row reads as a single visual unit. */
export function colorForStatus(s: DoctorCheck["status"]): string {
  switch (s) {
    case "fail":
      return "red";
    case "warn":
      return "yellow";
    case "ok":
      return "green";
  }
}

/** Subtitle: "all healthy" when problemCount===0, else the number.
 *  Kept terse so the title bar reads at a glance — when something is
 *  wrong, "Doctor · 2" is the load-bearing signal; the row body
 *  carries the names. */
export function formatSubtitle(problemCount: number): string {
  if (problemCount === 0) return "all healthy";
  return String(problemCount);
}
